import { z } from "zod";
/**
 * **The daily reward seam** — architecture §16o **A103 §6** (as amended
 * 2026-08-06), Ivy's lobby spec §21, LOCKED rule 37.
 *
 * The server owns the **state machine**; the client renders it and derives
 * nothing — no client-side eligibility, no client-side "today", no optimistic
 * transition (Ivy §21.3). Two routes, both on `api` and never on `game`, both
 * under `/api/v1/*` and therefore already inside the Origin guard's scope
 * (LOCKED rule 19 as widened by A27).
 *
 * ── What is NOT in these payloads, and why that is structural ──────────────
 *
 * **`claimedAt` is absent from both responses, in every state** — ruled for Ivy
 * (A103 §6 item 4, §16o addendum item 2). A claim *time* identifies the alias
 * sibling's claim as precisely as a name would (Ivy §21.4), and a field the
 * client must never render is a leak held shut by discipline. Deleting it holds
 * it shut by construction: the client cannot render what it was not sent, and a
 * future "show when you claimed" ticket has to change this contract to get it —
 * which is exactly the review that idea should face. The column still exists on
 * `coin_grant_claims` for support and for the integrity job; it does not cross
 * this seam. **A `claimedAt` in a served body is a blocking finding, not a spare
 * field** (A103 acceptance criterion 13).
 *
 * Fastify compiles these schemas into serialisers that drop unknown keys, so
 * this file is also the enforcement, not merely the documentation.
 */
/**
 * The six states. **This list is the contract, not the copy** — the strings are
 * Theo's, gated by Nora, placed by Ivy.
 *
 * There is no seventh state: `claiming` is a client-local in-flight flag, and
 * `credited` is a field on the claim response because it is a fact about *that
 * response* rather than about the day (A103 §6 item 1).
 */
export const DAILY_REWARD_STATES = [
    /** `economy.daily_reward_coins <= 0`. The client renders **nothing**, not a zero. */
    "disabled",
    /** Anonymous player — no mailbox, no reward. A103 §8's conversion-pitch surface. */
    "ineligible_guest",
    /** Registered, e-mail not verified. */
    "ineligible_unverified",
    /** Eligible, no settled game yet in this UTC day. */
    "needs_play",
    /** Eligible, played, no claim for this mailbox-day. */
    "claimable",
    /**
     * A claim exists for this mailbox-day — **by this account or by an alias
     * sibling. The state does not distinguish, and must not** (A103 §6, Ivy §21.4).
     */
    "claimed",
];
export const dailyRewardStateSchema = z.enum(DAILY_REWARD_STATES);
export const dailyRewardSchema = z.object({
    state: dailyRewardStateSchema,
    /**
     * `economy.daily_reward_coins`, **present and truthful in every state**,
     * including `ineligible_guest` and `ineligible_unverified` (A103 §6 item 2 /
     * Ivy §21.10 item 2): the guest pitch renders the number, and a numberless
     * offer is a weaker pitch for no security gain — the amount is a published
     * economy value, not a secret.
     *
     * The client never substitutes a constant. When Aaron flips the key, every
     * surface moves with it (Ivy §21.3 rule 2).
     */
    amountCoins: z.number().int().min(0),
    /**
     * The next UTC midnight, as an ISO instant. **Server-supplied; the client
     * renders the distance to it and never computes "today"** (Ivy §21.6 LOCKED,
     * the same discipline as LOCKED rule 31). A client whose clock is wrong must
     * not be able to show a claim affordance the server would refuse, nor hide one
     * it would accept.
     *
     * Present on every response of either route — the strongest reading of A103 §6
     * item 3's *"present in all five rendered states"*, and the one that cannot
     * rot as states are added.
     */
    resetsAt: z.string(),
});
export const dailyRewardClaimSchema = dailyRewardSchema.extend({
    /**
     * **Did THIS response credit?** An explicit, named field — never inferred from
     * a state delta (A103 §6 item 1, Ivy §21.10 item 1). The endpoint is
     * idempotent by construction: call it ten times and `credited` is `true` on at
     * most one of the ten, and at most once per mailbox-day across every account
     * on that mailbox.
     *
     * Ivy §21.4's anti-leak rule hangs on this being readable directly: the client
     * may use the first person **only** in the moment its own claim returned
     * `credited === true`. Every other rendering of `claimed` — a fresh mount, a
     * refetch, a `credited === false` response — is a statement about the day.
     */
    credited: z.boolean(),
});
//# sourceMappingURL=rewards.js.map